// Auth state: Firebase for identity, the gait API for authorisation.
//
// Signing in with Firebase is only half of it. The API's `users` table decides
// whether an authenticated person may do anything at all, and with what role —
// a valid token with no row there is rejected with 403. So after Firebase
// resolves we call /me, and it is /me's answer that gates the UI.
//
// The two states this distinguishes matter: "not signed in" sends you to the
// login screen, while "signed in but not approved" must NOT, or the user is
// bounced back to a login form that will keep succeeding and keep bouncing them.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import {
  api,
  ApiError,
  forgetInflight,
  prefetchApi,
  RECENT_SESSIONS_PATH,
  UNAUTHORIZED_EVENT,
} from './api';
import { auth } from './firebase';
import type { Me } from './types';

type Status = 'loading' | 'signed-out' | 'unapproved' | 'ready';

interface AuthValue {
  status: Status;
  /** The Firebase identity, once resolved. */
  firebaseUser: User | null;
  /** The API's view of this person: role and status. Null until approved. */
  me: Me | null;
  isAdmin: boolean;
  /** Why the API refused, when status is 'unapproved'. */
  reason: string | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    // A build with no Firebase config has no SDK to subscribe to. Resolve to
    // signed-out with an explanation rather than hanging on 'loading' for ever,
    // which is what a blank screen actually is.
    if (!auth) {
      setStatus('signed-out');
      return;
    }
    // /me is awaited, so an older callback can still be waiting on it when the
    // next sign-in or sign-out arrives. Each callback numbers itself and only
    // the newest may commit, or a stale identity lands over the current one.
    let seq = 0;
    // Fires once on load with the restored session (or null), then on every
    // sign-in and sign-out.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const mine = ++seq;
      // Nothing requested for the previous identity may reach this one.
      forgetInflight();
      setFirebaseUser(user);
      if (!user) {
        setMe(null);
        setReason(null);
        setStatus('signed-out');
        return;
      }
      setStatus('loading');
      setMe(null);
      setReason(null);
      // The dashboard is the first screen and asks for this the moment it
      // mounts. Starting it alongside /me means it is usually back by then,
      // instead of queueing behind the first render. If /me refuses, the
      // server refuses this too, and nothing awaits it.
      prefetchApi(RECENT_SESSIONS_PATH);
      try {
        const fresh = await api<Me>('/me');
        if (mine !== seq) return;
        setMe(fresh);
        setStatus('ready');
      } catch (err) {
        if (mine !== seq) return;
        // 403 is the interesting one: Firebase is happy, the API is not. That's
        // an account awaiting approval or suspended, and the person needs to be
        // told which — not silently returned to a login form.
        if (err instanceof ApiError && err.status === 403) {
          setReason(err.message);
          setStatus('unapproved');
          return;
        }
        setReason(err instanceof Error ? err.message : 'Could not reach the server.');
        setStatus('unapproved');
      }
    });
    return () => {
      seq++;
      unsubscribe();
    };
  }, []);

  // A 401 from any later request means the token went stale or was revoked
  // (an admin suspending someone kills their live session server-side).
  useEffect(() => {
    const onUnauthorized = () => auth && void fbSignOut(auth);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const value: AuthValue = {
    status,
    firebaseUser,
    me,
    isAdmin: me?.role === 'admin',
    reason,
    signOut: async () => {
      if (auth) await fbSignOut(auth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
